import React, { useState, useEffect } from "react";
import {
  Table,
  Input,
  Button,
  Modal,
  Form,
  Select,
  message,
  Space,
  Popconfirm,
  Typography,
  Card,
  Tag,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import axios from "axios";

const { Title } = Typography;
const { Option } = Select;

const CategoryMaintenance = () => {
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState(null);
  const [searchText, setSearchText] = useState("");

  const API_BASE_URL = "https://techcrew.pk/ulc/";

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    filterCategories();
  }, [categories, searchText]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}Get_Categories.php`);

      if (data.status && data.status.code === 200) {
        setCategories(data.data);
      } else {
        message.error(data.status?.message || "Failed to fetch categories");
      }
    } catch (error) {
      console.error("API Error:", error);
      message.error(
        error.response?.data?.message || "Failed to connect to server"
      );
    } finally {
      setLoading(false);
    }
  };

  const filterCategories = () => {
    let filtered = [...categories];

    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((item) =>
        item.category_name.toLowerCase().includes(searchLower)
      );
    }

    setFilteredCategories(filtered);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const url = editingId
        ? `${API_BASE_URL}Update_Category.php`
        : `${API_BASE_URL}Add_Category.php`;

      const payload = editingId
        ? { ...values, category_id: editingId }
        : values;

      const { data } = await axios.post(url, payload);

      if (data.status.code === 200) {
        message.success(data.status.message);
        setModalVisible(false);
        fetchCategories();
      } else {
        throw new Error(data.status.message);
      }
    } catch (error) {
      console.error("Submission error:", error);
      message.error(error.message || "Error saving category");
    }
  };

  const handleDelete = async (id) => {
    try {
      const { data } = await axios.post(`${API_BASE_URL}Delete_Category.php`, {
        category_id: id,
      });

      if (data.status.code === 200) {
        message.success("Category deactivated");
        fetchCategories();
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      message.error(error.message || "Deactivation failed");
    }
  };

  const columns = [
    {
      title: "Category Name",
      dataIndex: "category_name",
      key: "name",
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "Active" ? "green" : "red"}>{status}</Tag>
      ),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              setEditingId(record.category_id);
              form.setFieldsValue({
                category_name: record.category_name,
                status: record.status,
              });
              setModalVisible(true);
            }}
          />
          <Popconfirm
            title="Deactivate this category?"
            onConfirm={() => handleDelete(record.category_id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ paddingLeft: "250px", paddingTop: "60px" }}>
      <Card
        title={<Title level={4}>Category Management</Title>}
        bordered={false}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.resetFields();
              setEditingId(null);
              setModalVisible(true);
            }}
          >
            Add Category
          </Button>
        }
      >
        <div style={{ marginBottom: 16 }}>
          <Input
            placeholder="Search categories..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            style={{ width: 300 }}
          />
        </div>

        <Table
          columns={columns}
          dataSource={filteredCategories}
          rowKey="category_id"
          loading={loading}
          scroll={{ x: true }}
          bordered
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingId ? "Edit Category" : "Add New Category"}
        visible={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={600}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="category_name"
            label="Category Name"
            rules={[{ required: true, message: "Please enter category name" }]}
          >
            <Input placeholder="Enter category name" />
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            initialValue="Active"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="Active">Active</Option>
              <Option value="Inactive">Inactive</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CategoryMaintenance;
